import { debounce } from "lodash-es"
import { useEffect, useState } from "react"
import { type Store, type AnyAction } from "redux"
import { type SignHistoryState } from "./sign_history"
import { isTablet } from "@/common/util"
import type Sign from "@/signomatic/app/sign"
import { type SignStateData, type MetaSignTypeChange } from "@/signomatic/app/sign"
import { actions } from "@/signomatic/store/actions"

export const useSignState = (sign?: Sign) => {
  const [state, setState] = useState<SignState | undefined>(sign?.state)

  useEffect(() => {
    const update = ({ state }: SignStateData) => setState(state)
    sign?.onSignDataChanged.subscribe(update)

    return () => {
      sign?.onSignDataChanged.unsubscribe(update)
    }
  }, [sign, sign?.onSignDataChanged])

  return state
}

class StateBridge {
  private readonly store: Store

  private debouncedUpdateState = debounce(
    ({ state, data }: SignStateData) => {
      this.dispatch(actions.setSignState(state, data))
    },
    60,
    { leading: false }
  )

  constructor(store: Store, sign: Sign) {
    this.store = store

    sign.onSignTypeChanged.subscribe(this.handleSignTypeChange)
    sign.onMetaSignTypeChanged.subscribe(this.handleMetaSignTypeChange)
    sign.onSignDataChanged.subscribe(this.debouncedUpdateState)
    sign.onActiveChanged.subscribe(this.handleActiveChanged)
    sign.onEmptyClick.subscribe(this.handleEmptyClick)
    sign.onHistoryChanged.subscribe(this.handleHistoryChange)
    sign.onInlineEdit.subscribe(this.handleInlineEdit)
  }

  private handleActiveChanged = (state: SignObjectState) => {
    this.dispatch(actions.setActiveObject(state))
  }

  private handleSignTypeChange = (type: string) => {
    this.dispatch(actions.setSignType(type))
  }
  private handleMetaSignTypeChange = (info: MetaSignTypeChange) => {
    this.dispatch(actions.setMetaSignType(info.metaProduct, info.signType))
  }

  private handleEmptyClick = () => {
    this.dispatch(actions.resetToolbox())

    if (isTablet()) {
      this.dispatch(actions.setToolboxHeight(0))

      const menurow = document.querySelector(".menurow") as HTMLElement
      menurow.style.display = "flex"
    }
  }

  private handleHistoryChange = (history: SignHistoryState) => {
    this.dispatch(actions.setHistory(history))
  }

  private dispatch(action: AnyAction) {
    this.store.dispatch(action)
  }

  private handleInlineEdit = () => {
    this.dispatch(actions.resetSizeToolbox())
  }
}

export default StateBridge
